import PropTypes from 'prop-types';
import React from 'react';

import DataConnector from 'connect/DataConnector';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EntryProtocol} from 'notebook/ELNConstants';
import EntryExportConfig, {EntryExportConfigShape} from 'notebook/entries/EntryExportConfig';
import EntryExportMetadata from 'notebook/entries/EntryExportMetadata';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import {CidIdMap} from 'notebook/notes/NoteConstants';
// @ts-expect-error
import ProtocolEditor from 'notebook/protocols/editor/Editor';
import isConfiguration from 'notebook/util/isConfiguration';
import memoizeLastCall from 'util/memoizeLastCall';
import StageEntryNoteView from 'workflows/stage-entries/StageEntryNoteView';
import StageEntryStore from 'workflows/stage-entries/StageEntryStore';
import {StageEntryDocument} from 'workflows/WorkflowConstants';

interface EnhancedProps {
  editorState: {
    stageEntryDocument: StageEntryDocument;
    cidIdMap: CidIdMap;
    isLatest: boolean;
  };
}

class StageEntryPrintViewConnector extends DataConnector<BaseStageEntryPrintViewProps, EnhancedProps> {
  shouldSubscribeToRedux = false;
  stores = [StageEntryStore];

  enhanceProps(): EnhancedProps {
    return {editorState: this.stageEntryStore().getEditorState(this.props.id)};
  }
}

interface BaseStageEntryPrintViewProps {
  id: string;
  exportConfig: EntryExportConfig;
  showProtocols: Array<number>;
  indexSlice: {start: number; end: number};
}

const contextValueFn = memoizeLastCall((id: string) => ({
  showFormulas: false,
  fileId: id,
  isPrintPreviewView: true,
  isConfig: isConfiguration(id),
}));

class StageEntryPrintView extends React.Component<BaseStageEntryPrintViewProps & EnhancedProps> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    id: PropTypes.string.isRequired,
    exportConfig: PropTypes.shape(EntryExportConfigShape),
    showProtocols: PropTypes.array.isRequired,
    indexSlice: PropTypes.shape({
      start: PropTypes.number.isRequired,
      end: PropTypes.number.isRequired,
    }),
    editorState: PropTypes.object.isRequired,
  };

  renderEditor(): JSX.Element | null {
    const {stageEntryDocument, cidIdMap, isLatest} = this.props.editorState;
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const shouldRender = (protocol: EntryProtocol) =>
      this.props.showProtocols.includes(cidIdMap[protocol.cid] as number) && protocol.externalFile == null;
    if (stageEntryDocument == null) {
      return null;
    }

    return (
      <div>
        <EditorPropertiesContext.Provider value={contextValueFn(this.props.id)}>
          <div className="printable-page-container">
            <StageEntryNoteView
              beforeEntry={
                this.props.exportConfig ? (
                  <EntryExportMetadata entry={stageEntryDocument} exportConfig={this.props.exportConfig} />
                ) : null
              }
              fileId={this.props.id}
              humanId={stageEntryDocument.humanId}
              fileType={ModelTypeConstants.STAGE_ENTRY}
              note={stageEntryDocument.entryVersion.note}
              parentType="Entry"
              title={stageEntryDocument.name}
              isLatest={isLatest}
              permissions={stageEntryDocument.permissions}
              // Layout for print
              showTimestamps={false}
              indexSlice={this.props.indexSlice}
              disableFormatting={true}
              hideTitle={false}
              locked={true}
              allowEdits={false}
              preview={{showOpenLink: false}}
              viewer={true}
              restoreState={() => {}}
              storeState={() => {}}
              // Display Options
              printOptions={[]}
              printLayout={false}
              showPrintMargins={false}
              spellCheck={false}
              showTableOfContents={false}
            />
          </div>
          {stageEntryDocument.entryVersion.entryProtocols
            .filter((protocol) => shouldRender(protocol))
            .map((protocol) => (
              <div key={protocol.cid} className="printable-page-container">
                <ProtocolEditor
                  fileId={this.props.id}
                  protocol={protocol}
                  locked={true}
                  storeState={() => {}}
                  restoreState={() => {}}
                  requestSelection={() => {}}
                  viewer={true}
                  showPrintMargins={false}
                />
              </div>
            ))}
        </EditorPropertiesContext.Provider>
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <PrintableWrapper
        title={
          this.props.editorState.stageEntryDocument != null
            ? this.props.editorState.stageEntryDocument.name
            : undefined
        }
      >
        {this.renderEditor()}
      </PrintableWrapper>
    );
  }
}

export default StageEntryPrintViewConnector.connect(StageEntryPrintView);
